import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Trash2, Ban, Plus } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import { formatSlot } from "@/lib/formatSlot";

import { API } from "@/lib/apiBase";

const toDateStr = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const AdminBlocks = () => {
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("upcoming");
  const [courtFilter, setCourtFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  // Add-block dialog state
  const [addOpen, setAddOpen] = useState(false);
  const [courts, setCourts] = useState([]);
  const [form, setForm] = useState({
    courtId: "",
    date: new Date(),
    slots: new Set(),
    publicNote: "",
    privateNote: "",
  });
  const [formLoading, setFormLoading] = useState(false);
  const [daySlots, setDaySlots] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const fetchBlocks = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/blocks`);
      setBlocks(res.data || []);
    } catch {
      toast.error("Failed to load blocks");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCourts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/courts`);
      setCourts(res.data || []);
      if (res.data?.length && !form.courtId) {
        setForm((f) => ({ ...f, courtId: res.data[0].id }));
      }
    } catch {
      /* noop */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchBlocks();
    fetchCourts();
  }, [fetchBlocks, fetchCourts]);

  // Load day availability for the court+date selection
  useEffect(() => {
    if (!addOpen || !form.courtId || !form.date) return;
    let cancelled = false;
    setFormLoading(true);
    axios
      .get(
        `${API}/bookings/court/${form.courtId}/availability?date=${toDateStr(form.date)}`
      )
      .then((r) => {
        if (!cancelled) setDaySlots(r.data.all_slots || []);
      })
      .catch(() => {
        if (!cancelled) setDaySlots([]);
      })
      .finally(() => {
        if (!cancelled) setFormLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [addOpen, form.courtId, form.date]);

  const toggleSlot = (slot) =>
    setForm((f) => {
      const next = new Set(f.slots);
      if (next.has(slot)) next.delete(slot);
      else next.add(slot);
      return { ...f, slots: next };
    });

  const resetForm = () =>
    setForm({
      courtId: courts[0]?.id || "",
      date: new Date(),
      slots: new Set(),
      publicNote: "",
      privateNote: "",
    });

  const submitBlock = async () => {
    if (!form.courtId || form.slots.size === 0) {
      toast.error("Select at least one slot");
      return;
    }
    if (!form.privateNote.trim()) {
      toast.error("Private note is required (e.g. client name)");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/admin/blocks`, {
        court_id: form.courtId,
        booking_date: toDateStr(form.date),
        time_slots: Array.from(form.slots),
        public_note: form.publicNote || null,
        private_note: form.privateNote.trim(),
      });
      toast.success(`${form.slots.size} slot(s) blocked`);
      setAddOpen(false);
      resetForm();
      fetchBlocks();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to block");
    } finally {
      setSubmitting(false);
    }
  };

  const deleteOne = async (id) => {
    if (!window.confirm("Remove this block? The slot becomes available again."))
      return;
    try {
      await axios.delete(`${API}/admin/blocks/${id}`);
      toast.success("Block removed");
      setSelected((s) => {
        const next = new Set(s);
        next.delete(id);
        return next;
      });
      fetchBlocks();
    } catch {
      toast.error("Failed to remove block");
    }
  };

  const deleteSelected = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Remove ${selected.size} block(s)? Those slots become available again.`))
      return;
    setBulkBusy(true);
    const ids = Array.from(selected);
    let ok = 0;
    let fail = 0;
    // Fire in parallel — admin blocks endpoint handles each independently.
    await Promise.all(
      ids.map((id) =>
        axios
          .delete(`${API}/admin/blocks/${id}`)
          .then(() => {
            ok += 1;
          })
          .catch(() => {
            fail += 1;
          })
      )
    );
    setBulkBusy(false);
    setSelected(new Set());
    if (ok > 0) toast.success(`Removed ${ok} block${ok > 1 ? "s" : ""}`);
    if (fail > 0) toast.error(`Failed to remove ${fail} block${fail > 1 ? "s" : ""}`);
    fetchBlocks();
  };

  const todayStr = toDateStr(new Date());

  const filtered = blocks.filter((b) => {
    if (filter === "upcoming" && b.booking_date < todayStr) return false;
    if (filter === "past" && b.booking_date >= todayStr) return false;
    if (courtFilter !== "all" && b.court_id !== courtFilter) return false;
    if (dateFilter && b.booking_date !== dateFilter) return false;
    return true;
  });

  // Select-all applies only to currently-visible rows (the filtered set).
  const allVisibleSelected =
    filtered.length > 0 && filtered.every((b) => selected.has(b.id));
  const someVisibleSelected =
    filtered.some((b) => selected.has(b.id)) && !allVisibleSelected;

  const toggleRow = (id) =>
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleAllVisible = () =>
    setSelected((s) => {
      const next = new Set(s);
      if (allVisibleSelected) {
        filtered.forEach((b) => next.delete(b.id));
      } else {
        filtered.forEach((b) => next.add(b.id));
      }
      return next;
    });

  const filters = [
    { key: "upcoming", label: "Upcoming" },
    { key: "past", label: "Past" },
    { key: "all", label: "All" },
  ];

  return (
    <div data-testid="admin-blocks-content">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
        <div>
          <h2 className="text-3xl font-bold flex items-center gap-3">
            <Ban className="text-orange-400" size={26} />
            Manual Blocks
          </h2>
          <p className="text-xs text-[#a1a1aa] mt-1">
            Slots you've blocked from customer bookings (maintenance, tournaments, etc.)
          </p>
        </div>
        <button
          data-testid="add-block-button"
          onClick={() => {
            resetForm();
            setAddOpen(true);
          }}
          className="inline-flex items-center gap-2 px-4 py-2 text-xs font-bold bg-[#8cc63f] text-black rounded-sm hover:bg-[#9ed15c] transition-colors"
        >
          <Plus size={14} />
          Block slots
        </button>
      </div>

      <div className="card-surface p-6">
        <div className="flex flex-wrap gap-2 mb-4 pb-4 border-b border-white/10">
          {filters.map((f) => {
            const count = blocks.filter((b) => {
              if (f.key === "upcoming") return b.booking_date >= todayStr;
              if (f.key === "past") return b.booking_date < todayStr;
              return true;
            }).length;
            const active = filter === f.key;
            return (
              <button
                key={f.key}
                data-testid={`block-filter-${f.key}`}
                onClick={() => setFilter(f.key)}
                className={`px-3 py-1.5 text-xs font-semibold rounded-sm border transition-colors flex items-center gap-2 ${
                  active
                    ? "bg-[#8cc63f] text-black border-[#8cc63f]"
                    : "bg-transparent text-[#a1a1aa] border-white/10 hover:border-[#8cc63f] hover:text-white"
                }`}
              >
                {f.label}
                <span
                  className={`px-1.5 py-0.5 rounded-sm text-[10px] font-bold ${
                    active ? "bg-black/20 text-black" : "bg-white/10 text-white"
                  }`}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Secondary filters: court + date. Combined via AND with the
            upcoming/past pill above. */}
        <div className="flex flex-wrap items-end gap-3 mb-4 pb-4 border-b border-white/10">
          <div className="flex flex-col gap-1">
            <label className="label-text text-[10px]" htmlFor="block-court-filter">
              Court
            </label>
            <select
              id="block-court-filter"
              data-testid="block-court-filter"
              value={courtFilter}
              onChange={(e) => setCourtFilter(e.target.value)}
              className="bg-[#0a0a0a] text-white text-xs font-semibold border border-white/15 rounded-sm px-2 py-1.5 hover:border-[#8cc63f]/60 focus:border-[#8cc63f] focus:outline-none min-w-[140px]"
            >
              <option value="all">All courts</option>
              {courts.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="label-text text-[10px]" htmlFor="block-date-filter">
              Date
            </label>
            <input
              id="block-date-filter"
              data-testid="block-date-filter"
              type="date"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="bg-[#0a0a0a] text-white text-xs font-semibold border border-white/15 rounded-sm px-2 py-1.5 hover:border-[#8cc63f]/60 focus:border-[#8cc63f] focus:outline-none"
            />
          </div>
          {(courtFilter !== "all" || dateFilter) && (
            <button
              type="button"
              data-testid="block-clear-filters"
              onClick={() => {
                setCourtFilter("all");
                setDateFilter("");
              }}
              className="px-3 py-1.5 text-xs font-bold text-[#a1a1aa] border border-white/15 rounded-sm hover:border-red-400 hover:text-red-400 transition-colors"
            >
              Clear filters
            </button>
          )}
          <p className="ml-auto text-xs text-[#a1a1aa] self-center">
            Showing <span className="text-white font-bold">{filtered.length}</span> of {blocks.length}
          </p>
        </div>

        {/* Bulk-delete toolbar — only appears when rows are selected. */}
        {selected.size > 0 && (
          <div
            data-testid="block-bulk-toolbar"
            className="flex items-center justify-between gap-2 mb-4 px-3 py-2 rounded-sm bg-red-500/10 border border-red-500/40"
          >
            <p className="text-xs font-bold text-red-300">
              {selected.size} block{selected.size > 1 ? "s" : ""} selected
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                data-testid="block-clear-selection"
                onClick={() => setSelected(new Set())}
                className="px-3 py-1 text-xs font-bold text-[#a1a1aa] hover:text-white transition-colors"
              >
                Clear
              </button>
              <button
                type="button"
                data-testid="block-bulk-delete"
                disabled={bulkBusy}
                onClick={deleteSelected}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold bg-red-500 text-white rounded-sm hover:bg-red-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Trash2 size={12} />
                {bulkBusy ? "Removing…" : `Remove ${selected.size}`}
              </button>
            </div>
          </div>
        )}

        {loading ? (
          <div className="text-center py-10">
            <div className="inline-block w-6 h-6 border-4 border-[#8cc63f] border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : filtered.length === 0 ? (
          <p className="text-center text-[#a1a1aa] py-12 text-sm">
            No manual blocks.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left py-3 px-2 w-10">
                    <input
                      type="checkbox"
                      data-testid="block-select-all"
                      aria-label="Select all visible blocks"
                      checked={allVisibleSelected}
                      ref={(el) => {
                        if (el) el.indeterminate = someVisibleSelected;
                      }}
                      onChange={toggleAllVisible}
                      className="w-4 h-4 accent-[#8cc63f] cursor-pointer"
                    />
                  </th>
                  <th className="text-left py-3 px-2 label-text">Court</th>
                  <th className="text-left py-3 px-2 label-text">Date</th>
                  <th className="text-left py-3 px-2 label-text">Time</th>
                  <th className="text-left py-3 px-2 label-text">Public note</th>
                  <th className="text-left py-3 px-2 label-text">Private note</th>
                  <th className="text-left py-3 px-2 label-text">Created</th>
                  <th className="text-left py-3 px-2 label-text">Action</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((b) => {
                  const pub = b.public_note ?? b.note;
                  const priv = b.private_note;
                  return (
                  <tr
                    key={b.id}
                    className={`border-b border-white/5 ${
                      selected.has(b.id) ? "bg-red-500/5" : ""
                    }`}
                  >
                    <td className="py-3 px-2">
                      <input
                        type="checkbox"
                        data-testid={`block-select-${b.id}`}
                        aria-label={`Select block ${b.id}`}
                        checked={selected.has(b.id)}
                        onChange={() => toggleRow(b.id)}
                        className="w-4 h-4 accent-[#8cc63f] cursor-pointer"
                      />
                    </td>
                    <td className="py-3 px-2 font-semibold text-[#8cc63f]">
                      {b.court_name}
                    </td>
                    <td className="py-3 px-2">{b.booking_date}</td>
                    <td className="py-3 px-2 whitespace-nowrap">
                      {formatSlot(b.time_slot)}
                    </td>
                    <td
                      className="py-3 px-2"
                      data-testid={`block-public-note-${b.id}`}
                    >
                      {pub ? (
                        <span className="inline-block px-2 py-0.5 rounded-sm text-xs font-semibold bg-orange-500/20 text-orange-300 border border-orange-500/40">
                          {pub}
                        </span>
                      ) : (
                        <span className="text-[#a1a1aa] text-xs">Booked</span>
                      )}
                    </td>
                    <td
                      className="py-3 px-2"
                      data-testid={`block-private-note-${b.id}`}
                    >
                      {priv ? (
                        <span className="inline-block px-2 py-0.5 rounded-sm text-xs font-semibold bg-white/5 text-[#e4e4e7] border border-white/15">
                          {priv}
                        </span>
                      ) : (
                        <span className="text-[#a1a1aa] text-xs">—</span>
                      )}
                    </td>
                    <td className="py-3 px-2 text-[#a1a1aa] text-xs">
                      {b.created_at ? b.created_at.slice(0, 10) : "—"}
                    </td>
                    <td className="py-3 px-2">
                      <button
                        data-testid={`delete-block-${b.id}`}
                        onClick={() => deleteOne(b.id)}
                        className="p-1.5 text-[#a1a1aa] hover:text-red-500 transition-colors"
                        title="Remove this block"
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add-block dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="bg-[#121212] text-white max-w-xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-black tracking-tighter flex items-center gap-2">
              <Ban size={22} className="text-orange-400" />
              Block Time Slots
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="label-text block mb-2">Court</label>
                <select
                  data-testid="add-block-court"
                  value={form.courtId}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, courtId: e.target.value, slots: new Set() }))
                  }
                  className="input-field text-sm w-full"
                >
                  {courts.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
                <label className="label-text block mt-3 mb-2">
                  Public note <span className="text-[#a1a1aa] font-normal">(optional)</span>
                </label>
                <input
                  data-testid="add-block-public-note"
                  type="text"
                  maxLength={80}
                  value={form.publicNote}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, publicNote: e.target.value }))
                  }
                  placeholder="e.g. Venue Rental, Tournament/Dual Meet, Social Event"
                  className="input-field text-sm"
                />
                <label className="label-text block mt-3 mb-2">
                  Private note <span className="text-[#ff6b6b] font-semibold">(mandatory)</span>
                </label>
                <input
                  data-testid="add-block-private-note"
                  type="text"
                  required
                  maxLength={120}
                  value={form.privateNote}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, privateNote: e.target.value }))
                  }
                  placeholder="e.g. Client name or other remarks"
                  className="input-field text-sm"
                />
              </div>
              <div>
                <label className="label-text block mb-2">Date</label>
                <Calendar
                  mode="single"
                  selected={form.date}
                  onSelect={(d) =>
                    d && setForm((f) => ({ ...f, date: d, slots: new Set() }))
                  }
                  modifiersClassNames={{
                    selected: "assenda-day-selected",
                    today: "assenda-day-today",
                  }}
                  className="rounded-md border border-white/10"
                />
              </div>
            </div>

            <div>
              <label className="label-text block mb-2">
                Time slots{" "}
                <span className="text-[#a1a1aa] font-normal">
                  (tap to select — already-blocked/booked slots are disabled)
                </span>
              </label>
              {formLoading ? (
                <div className="text-center py-6">
                  <div className="inline-block w-5 h-5 border-4 border-[#8cc63f] border-t-transparent rounded-full animate-spin"></div>
                </div>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-1.5 max-h-[30vh] overflow-y-auto pr-1">
                  {daySlots.map((s) => {
                    const available = s.status === "available";
                    const selected = form.slots.has(s.time_slot);
                    return (
                      <button
                        key={s.time_slot}
                        type="button"
                        data-testid={`add-block-slot-${s.time_slot}`}
                        disabled={!available}
                        onClick={() => toggleSlot(s.time_slot)}
                        className={`px-2 py-1.5 text-[11px] font-semibold rounded-sm border transition-colors text-center ${
                          !available
                            ? "bg-[#1a1a1a] text-[#666] border-white/5 cursor-not-allowed"
                            : selected
                            ? "bg-[#8cc63f] text-black border-[#8cc63f]"
                            : "bg-[#0a0a0a] text-white border-white/10 hover:border-[#8cc63f]"
                        }`}
                      >
                        {formatSlot(s.time_slot)}
                      </button>
                    );
                  })}
                </div>
              )}
              {form.slots.size > 0 && (
                <p className="text-xs text-[#8cc63f] mt-2">
                  {form.slots.size} selected
                </p>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t border-white/10">
              <button
                type="button"
                data-testid="add-block-cancel"
                onClick={() => setAddOpen(false)}
                className="px-4 py-2 text-xs font-bold text-[#a1a1aa] border border-white/15 rounded-sm hover:border-white hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                data-testid="add-block-submit"
                onClick={submitBlock}
                disabled={submitting || form.slots.size === 0}
                className="px-4 py-2 text-xs font-bold bg-[#8cc63f] text-black rounded-sm hover:bg-[#9ed15c] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {submitting ? "Blocking…" : "Block slots"}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminBlocks;
